import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MegamenuComponent } from '../megamenu/megamenu.component';
import { FooterComponent } from '../footer/footer.component';
import { CompanyConfigService, CompanyConfig } from '../../services/company-config.service';
import { ConfigurationService } from '../../services/configuration.service';

interface JobPosition {
  title: string;
  department: string;
  type: string;
  location: string;
  description: string;
  responsibilities: string[];
  requirements: string[];
  benefits: string[];
  salary?: string;
  featured?: boolean;
}

@Component({
  selector: 'app-careers',
  templateUrl: './careers.component.html',
  styleUrl: './careers.component.css',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MegamenuComponent,
    FooterComponent
  ]
})
export class CareersComponent implements OnInit {
  private companyConfig = inject(CompanyConfigService);
  private configService = inject(ConfigurationService);

  config: CompanyConfig;
  openPositions: JobPosition[] = [];

  constructor() {
    // Load synchronous config immediately for backward compatibility
    this.config = this.companyConfig.getConfig();
  }

  async ngOnInit(): Promise<void> {
    await this.loadPositions();
  }

  private async loadPositions(): Promise<void> {
    try {
      // Get salary range from configuration
      const salaryRange = await this.configService.getStringConfig('SALES_SALARY_RANGE', '$60,000 - $80,000 + Commission');
      
      this.openPositions = [
    {
      title: 'Head of Sales',
      department: 'Sales & Marketing',
      type: 'Full-Time',
      location: 'Northern Utah',
      description: 'Lead our sales team and drive business growth across Northern Utah. We\'re looking for a dynamic leader who can build relationships, develop sales strategies, and make a real impact in our growing company.',
      responsibilities: [
        'Develop and implement comprehensive sales strategies',
        'Lead and mentor the sales team',
        'Build and maintain relationships with key clients',
        'Analyze market trends and identify growth opportunities',
        'Collaborate with operations team to ensure service delivery',
        'Manage sales forecasting and reporting',
        'Represent the company at industry events and networking functions'
      ],
      requirements: [
        '5+ years of sales leadership experience',
        'Proven track record of meeting and exceeding sales targets',
        'Experience in service-based industries preferred',
        'Strong leadership and team management skills',
        'Excellent communication and presentation abilities',
        'Bachelor\'s degree in Business, Marketing, or related field',
        'Valid driver\'s license and reliable transportation'
      ],
      benefits: [
        'Competitive base salary plus commission',
        'Health, dental, and vision insurance',
        'Paid time off and holidays',
        'Professional development opportunities',
        'Company vehicle allowance',
        'Performance bonuses'
      ],
      salary: salaryRange,
      featured: true
    },
    {
      title: 'Door to Door Sales Representative',
      department: 'Sales',
      type: 'Full-Time',
      location: 'Northern Utah',
      description: 'Join our dynamic sales team and help protect families from pests while building a rewarding career. Perfect for motivated individuals who enjoy meeting people and want unlimited earning potential.',
      responsibilities: [
        'Generate new business through door-to-door canvassing',
        'Present pest control solutions to homeowners',
        'Build rapport and trust with potential customers',
        'Schedule service appointments and follow up on leads',
        'Maintain accurate records of sales activities',
        'Meet or exceed monthly sales targets',
        'Provide excellent customer service and support'
      ],
      requirements: [
        'High school diploma or equivalent',
        'Strong communication and interpersonal skills',
        'Self-motivated with a positive attitude',
        'Ability to work independently and manage time effectively',
        'Comfortable with outdoor work in various weather conditions',
        'Valid driver\'s license and reliable transportation',
        'Previous sales experience preferred but not required'
      ],
      benefits: [
        'Competitive commission structure',
        'Weekly pay with bonus opportunities',
        'Comprehensive training program',
        'Health insurance options',
        'Flexible scheduling',
        'Career advancement opportunities'
      ],
      salary: '$40,000 - $70,000+ (Commission Based)',
      featured: false
    },
    {
      title: 'Licensed Exterminator',
      department: 'Operations',
      type: 'Full-Time',
      location: 'Northern Utah',
      description: 'Use your expertise to protect homes and businesses from pests. We\'re seeking licensed professionals who take pride in delivering exceptional service and solving pest problems effectively.',
      responsibilities: [
        'Conduct thorough property inspections for pest activity',
        'Apply pest control treatments safely and effectively',
        'Educate customers on pest prevention and treatment plans',
        'Maintain detailed service records and documentation',
        'Follow all safety protocols and regulatory requirements',
        'Provide excellent customer service and build relationships',
        'Identify and recommend additional services when appropriate'
      ],
      requirements: [
        'Valid Utah pest control license (required)',
        '2+ years of pest control experience',
        'Knowledge of pest biology and control methods',
        'Strong attention to detail and safety protocols',
        'Excellent customer service skills',
        'Physical ability to perform treatments in various environments',
        'Valid driver\'s license with clean driving record'
      ],
      benefits: [
        'Competitive hourly wage',
        'Health, dental, and vision insurance',
        'Paid time off and holidays',
        'Company vehicle and equipment provided',
        'Continuing education and training',
        'Retirement plan options'
      ],
      salary: '$18 - $25 per hour',
      featured: false
    }
      ];
    } catch (error) {
      console.warn('⚠️ Could not load configuration for positions, using defaults:', error);
      // Fallback positions with hardcoded salary
      this.openPositions = [
        // Add fallback positions here if needed
      ];
    }
  }

  companyBenefits = [
    {
      icon: 'school',
      title: 'Comprehensive Training',
      description: 'We provide thorough training to ensure your success'
    },
    {
      icon: 'trending_up',
      title: 'Career Growth',
      description: 'Clear advancement paths and promotion opportunities'
    },
    {
      icon: 'favorite',
      title: 'Health Benefits',
      description: 'Medical, dental, and vision insurance options'
    },
    {
      icon: 'schedule',
      title: 'Work-Life Balance',
      description: 'Flexible scheduling and paid time off'
    },
    {
      icon: 'groups',
      title: 'Team Environment',
      description: 'Supportive team culture and collaborative atmosphere'
    },
    {
      icon: 'attach_money',
      title: 'Competitive Pay',
      description: 'Fair compensation with performance incentives'
    }
  ];

  applyForPosition(position: JobPosition) {
    const subject = `Application for ${position.title} Position`;
    const body = `Hello,\n\nI am interested in applying for the ${position.title} position at Complete Pest Control.\n\nPlease find my resume attached.\n\nThank you for your consideration.\n\nBest regards,`;
    
    window.location.href = `mailto:${this.config.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  callPhone() {
    this.companyConfig.callPhone();
  }

  sendEmail() {
    this.companyConfig.sendEmail();
  }
}